import { Box, Flex, Icon, Text, useColorModeValue } from '@chakra-ui/react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { useState } from 'react';

const DAY_ABBR = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];
const MONTHS_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

interface MiniCalendarProps {
  selectedDate: Date | null;
  onSelect: (date: Date) => void;
  minDate?: Date;
}

function startOfMonth(y: number, m: number) {
  return new Date(y, m, 1);
}

function daysInMonth(y: number, m: number) {
  return new Date(y, m + 1, 0).getDate();
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

export function MiniCalendar({ selectedDate, onSelect, minDate }: MiniCalendarProps) {
  const today = new Date();
  const floor = minDate ?? today;

  const [view, setView] = useState<{ year: number; month: number }>({
    year: today.getFullYear(),
    month: today.getMonth(),
  });

  const borderColor  = useColorModeValue('gray.100', 'whiteAlpha.100');
  const headerColor  = useColorModeValue('gray.700', 'gray.100');
  const dayLabelColor = useColorModeValue('gray.400', 'gray.500');
  const mutedColor   = useColorModeValue('gray.300', 'gray.600');
  const todayRing    = useColorModeValue('brand.300', 'brand.600');
  const hoverBg      = useColorModeValue('gray.100', 'whiteAlpha.100');

  const { year, month } = view;

  const firstDayOfWeek = (() => {
    const d = startOfMonth(year, month).getDay();
    return d === 0 ? 6 : d - 1; // Monday-first
  })();

  const totalDays = daysInMonth(year, month);
  const cells: (number | null)[] = [
    ...Array(firstDayOfWeek).fill(null),
    ...Array.from({ length: totalDays }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const prevMonth = () => {
    setView(v => v.month === 0
      ? { year: v.year - 1, month: 11 }
      : { year: v.year, month: v.month - 1 });
  };
  const nextMonth = () => {
    setView(v => v.month === 11
      ? { year: v.year + 1, month: 0 }
      : { year: v.year, month: v.month + 1 });
  };

  return (
    <Box>
      {/* Month navigation */}
      <Flex align="center" justify="space-between" mb="12px">
        <Box
          as="button"
          onClick={prevMonth}
          p="4px"
          borderRadius="8px"
          _hover={{ bg: hoverBg }}
          color={headerColor}
          display="flex"
          alignItems="center"
        >
          <Icon as={MdChevronLeft} w="18px" h="18px" />
        </Box>
        <Text fontSize="sm" fontWeight="700" color={headerColor}>
          {MONTHS_ES[month]} {year}
        </Text>
        <Box
          as="button"
          onClick={nextMonth}
          p="4px"
          borderRadius="8px"
          _hover={{ bg: hoverBg }}
          color={headerColor}
          display="flex"
          alignItems="center"
        >
          <Icon as={MdChevronRight} w="18px" h="18px" />
        </Box>
      </Flex>

      {/* Day-of-week headers */}
      <Flex mb="6px">
        {DAY_ABBR.map(d => (
          <Box key={d} flex="1" textAlign="center">
            <Text fontSize="10px" fontWeight="700" color={dayLabelColor} textTransform="uppercase">
              {d}
            </Text>
          </Box>
        ))}
      </Flex>

      {/* Day grid */}
      {Array.from({ length: cells.length / 7 }, (_, row) => (
        <Flex key={row} mb="2px">
          {cells.slice(row * 7, row * 7 + 7).map((day, col) => {
            if (!day) return <Box key={col} flex="1" />;

            const date = new Date(year, month, day);
            const isPast   = date < new Date(floor.getFullYear(), floor.getMonth(), floor.getDate());
            const isToday  = isSameDay(date, today);
            const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;

            return (
              <Box key={col} flex="1" display="flex" justifyContent="center">
                <Box
                  as={isPast ? 'div' : 'button'}
                  onClick={isPast ? undefined : () => onSelect(date)}
                  w="32px"
                  h="32px"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor={isPast ? 'default' : 'pointer'}
                  bg={isSelected ? 'brand.500' : 'transparent'}
                  boxShadow={isToday && !isSelected ? `0 0 0 2px ${todayRing}` : undefined}
                  _hover={isPast ? {} : { bg: isSelected ? 'brand.600' : hoverBg }}
                  transition="all 0.12s"
                >
                  <Text
                    fontSize="sm"
                    fontWeight={isToday || isSelected ? '700' : '400'}
                    color={isSelected ? 'white' : isPast ? mutedColor : headerColor}
                    lineHeight="1"
                  >
                    {day}
                  </Text>
                </Box>
              </Box>
            );
          })}
        </Flex>
      ))}

      {/* Separator */}
      <Box borderTop="1px solid" borderColor={borderColor} mt="10px" />
    </Box>
  );
}

export default MiniCalendar;
